import React, { useCallback, useState } from "react";
import AddToRequisitionListQuery from "./AddToRequisitionListQuery.gql";
import RequisitionListCreateModal from "../RequisitionListModal/RequisitionListCreateModal";

/**
 * @callback AddToRequisitionListFunction
 * @param {string} [requisitionListId] if falsy will create a new requisition list (through a user popup) then add items to it
 */

/**
 * @typedef {object} InputProps
 * @property {import('./withAddMultipleItemsToRequisitionListMutation').AddToRequisitionListFunction} addToRequisitionList
 */

/**
 * @typedef {object} OutputProps
 * @property {AddToRequisitionListFunction} addToRequisitionList
 * @property {JSX.Element} newRequisitionListModal
 */

/**
 * @param {React.ComponentType<BaseProps & InputProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps & InputProps>}
 * @template BaseProps
 */
const withAddMultipleItemsToNewRequisitionListMutation =
  (BaseComponent) => (props) => {
    const { addToRequisitionList } = props;

    const [isNewRequisitionListModalOpen, setIsNewRequisitionListModalOpen] =
      useState(false);
    const [addToRequisitionListItems, setAddToRequisitionListItems] = useState(
      {}
    );

    const addToNewRequisitionList = useCallback((items) => {
      setAddToRequisitionListItems(items);
      setIsNewRequisitionListModalOpen(true);
    }, []);

    const addToRequisitionListOverride = useCallback(
      (requisitionListId, items) => {
        if (requisitionListId) {
          addToRequisitionList(requisitionListId, items);
        } else {
          addToNewRequisitionList(items);
        }
      },
      [addToRequisitionList, addToNewRequisitionList]
    );

    const newRequisitionListModal = (
      <RequisitionListCreateModal
        isOpen={isNewRequisitionListModalOpen}
        onRequestClose={() => setIsNewRequisitionListModalOpen(false)}
        readQuery={AddToRequisitionListQuery}
        onSuccess={({ id }) =>
          addToRequisitionList(id, addToRequisitionListItems)
        }
      />
    );

    return (
      <BaseComponent
        {...props}
        newRequisitionListModal={newRequisitionListModal}
        addToRequisitionList={addToRequisitionListOverride}
      />
    );
  };

export default withAddMultipleItemsToNewRequisitionListMutation;
